import React, { ComponentType, useLayoutEffect } from "react";
import { Box } from "@mui/material";
import { DotLegalDialog, DotLegalHeader } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import { useBodyScrollLock } from "@common/hooks/useBodyScrollLock";
import { isPlainLeftClick } from "@common/isPlainLeftClick";
import { isInternalLink } from "@common/isInternalLink";
import { useAiChatActions, useAiChatState } from "../../aiChat/AiChatContext";
import PaneFrame from "../rail/PaneFrame";
import { RailWidthState } from "../rail/Rail.hooks";
import { PanePositionProvider } from "./paneLayout/usePane";
import { paneStore } from "./paneStore";
import { Frame, FrameId, stageResponseToFollowedLink } from "./paneRules";
import { usePlacement, usePaneSlots } from "./usePanes";

/**
 * The click handler for a link being followed inside a pane. A link that handles the click itself, without
 * navigating, stops propagation so it never gets here (stepping between sibling tasks, opening a document
 * preview).
 */
function makeStageGiveWayToFollowedLink(hasPaneOnStage: boolean, isRailOccupied: boolean) {
    return (event: React.MouseEvent) => {
        if (!isPlainLeftClick(event) || !isInternalLink(event.target)) {
            return;
        }

        // Fields save as they are typed, so there is nothing to confirm before the pane goes.
        const response = stageResponseToFollowedLink(hasPaneOnStage, isRailOccupied);
        if (response === "close") {
            paneStore.closeSlot("stage");
        } else if (response === "collapse") {
            paneStore.collapseStage();
        }
    };
}

interface PaneInFrameProps {
    frameId: FrameId;
    frame: Frame;
    isRailOccupied: boolean;
    isStageOccupied: boolean;
    widthState: RailWidthState;
}

/**
 * One frame's pane. Always mounted so that opening and closing have something to slide, and bound to the
 * frame rather than to a slot: the frame keeps this pane as it moves between the rail and the stage, which
 * is what lets the move slide instead of remount.
 */
function PaneInFrame(props: PaneInFrameProps) {
    const pane = props.frame.pane;
    const Pane = pane?.definition.component as ComponentType<object> | undefined;

    return (
        <PaneFrame
            slot={pane ? props.frame.slot : "closed"}
            defaultSlot={props.frame.slot}
            widthState={props.widthState}
            isRailOccupied={props.isRailOccupied}
            isStageOccupied={props.isStageOccupied}
            resizeHandleColor={"primary.main"}
        >
            {pane && Pane && (
                <PanePositionProvider
                    value={{ frame: props.frameId, instanceId: pane.instanceId, slot: props.frame.slot, isStagePane: pane.definition.slot === "stage" }}
                >
                    {/* Keyed on the instance rather than the entity so that opening a second obligation over the
                        first, or the same one twice, mounts a fresh pane instead of inheriting its state. */}
                    <Pane key={pane.instanceId} {...pane.props} />
                </PanePositionProvider>
            )}
        </PaneFrame>
    );
}

function PaneHost() {
    const { translateString } = useTranslation();
    const { placement, confirming } = usePlacement();
    const { hasPaneOnRight, hasPaneOnStage } = usePaneSlots();
    const { isOpen: isChatOpen, widthState } = useAiChatState();
    const chatActions = useAiChatActions();

    // A layout effect so the connection is in place before any page's own effects open a pane on mount,
    // such as a task bound to the url.
    useLayoutEffect(() => paneStore.connectChat(chatActions), [chatActions]);

    // The stage pane's right edge and the spacer care about anything in the rail, the chat included.
    const isRailOccupied = hasPaneOnRight || isChatOpen;

    // A pane on the stage covers the page, drawer included; the wheel over it must not scroll what is
    // underneath. The chat keeps its own lock for its expanded mode.
    useBodyScrollLock(hasPaneOnStage);

    const onFollowedLink = makeStageGiveWayToFollowedLink(hasPaneOnStage, isRailOccupied);

    return (
        <>
            {/* Only listens for links being followed; `display: contents` keeps it out of the panes' layout. */}
            <Box sx={{ display: "contents" }} onClick={onFollowedLink}>
                <PaneInFrame frameId="first" frame={placement.first} isRailOccupied={isRailOccupied} isStageOccupied={hasPaneOnStage} widthState={widthState} />
                <PaneInFrame
                    frameId="second"
                    frame={placement.second}
                    isRailOccupied={isRailOccupied}
                    isStageOccupied={hasPaneOnStage}
                    widthState={widthState}
                />
            </Box>

            {/* Outside the panes, because what waits on this answer may be one of them going away. */}
            {confirming && (
                <DotLegalDialog
                    open
                    size="sm"
                    header={translateString("discardChangesHeader")}
                    buttonOkText={translateString("discardChanges")}
                    okButtonStyle="danger"
                    onOkClick={paneStore.confirmDiscard}
                    secondaryButtonText={translateString("cancel")}
                    onSecondaryButtonClick={paneStore.cancelDiscard}
                    onDialogClose={paneStore.cancelDiscard}
                >
                    {/* The header's own root is a flex row with nothing justifying it, so its text sits at the
                        start however it is aligned within itself. Centring it as a flex item is what shows. */}
                    <Box sx={{ "& > *": { justifyContent: "center" } }}>
                        <DotLegalHeader headerStyle={"small"} fontWeight={400} center>
                            {translateString("discardChangesText")}
                        </DotLegalHeader>
                    </Box>
                </DotLegalDialog>
            )}
        </>
    );
}

export default PaneHost;
